import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import BpkSectionHeader from '@skyscanner/backpack-web/bpk-component-section-header';

import CardRail from '../CardRail';
import SelectedChips from '../SelectedChips';
import ThemeCard from '../ThemeCard';

import type { CardRailWithThemeCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardRailWithThemeCardProps';
import type { Chip } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardProps';

import STYLES from './CardRailWithThemeCard.module.scss';

type CardRailWithThemeCardPropsWithExtraProps = CardRailWithThemeCardProps & {
  callToAction?: ReactNode;
};

const CardRailWithThemeCard = ({
  callToAction = null,
  cards,
  disclaimer,
  grouping,
  imageConfig,
  onCardClick,
  subtitle,
  title,
}: CardRailWithThemeCardPropsWithExtraProps) => {
  const [activeChipIndex, setActiveChipIndex] = useState(0);
  const [renderCards, setRenderCards] = useState(cards);
  const chips = grouping
    ? Array.from(
        new Map(
          cards
            .map((item) => item.groupKey)
            .filter((key): key is Chip => {
              if (key !== undefined) {
                return !!key?.name;
              }

              return false;
            })
            .map((key) => {
              const id = key?.name;
              return [id, key]; // 👈 id as the unique key
            }),
        ).values(),
      )
    : [];
  useEffect(() => {
    if (grouping) {
      if (activeChipIndex < chips.length) {
        setRenderCards(
          cards.filter((card) => {
            if (card.groupKey !== undefined) {
              return card.groupKey?.name === chips[activeChipIndex]?.name;
            }
            return false;
          }),
        );
      }
    }
  }, [activeChipIndex]);

  if (!cards.length) {
    return null;
  }

  return (
    <div className={STYLES.CardRailWithThemeCard}>
      <div className={STYLES.CardRailWithThemeCard__header}>
        <BpkSectionHeader title={title} description={subtitle} />
      </div>
      <SelectedChips
        chips={chips}
        activeChipIndex={activeChipIndex}
        setActiveChipIndex={setActiveChipIndex}
        cardType="Rail"
      />
      <CardRail disclaimer={disclaimer} callToAction={callToAction}>
        {renderCards.map((card, index) => (
          <div
            key={card.title}
            data-testid={`themeCardGroup${index}`}
            className={String(STYLES.CardRail__card)}
          >
            <ThemeCard
              {...card}
              index={index}
              imageConfig={imageConfig}
              onClick={onCardClick && (() => onCardClick(card))}
            />
          </div>
        ))}
      </CardRail>
    </div>
  );
};

export default CardRailWithThemeCard;
